/**
 * Check if an object is empty.
 * @param {Object} obj
 * @returns {boolean}
 */
const isEmptyObj = obj => !obj || Object.keys(obj).length === 0;

/**
 * Compare 2 single-level objects.
 * @param {Object} a
 * @param {Object} b
 * @return {boolean} Objects are equal or not.
 */
const compareObjs = (a, b) => {
	// If either object doesn't exist
	if (!a || !b) {
		return false;
	}

	// Create arrays of property names
	const aProps = Object.getOwnPropertyNames(a);
	const bProps = Object.getOwnPropertyNames(b);

	if (aProps.length !== bProps.length) {
		return false;
	}

	for (let i = aProps.length - 1; i >= 0; i--) {
		const key = aProps[i];
		if (a[key] !== b[key]) {
			return false;
		}
	}

	return true;
};

/**
 * Formats local date to local date string
 * @param {Date} localDate
 * @param {object} options - optional ;)
 * @returns {string}
 */
const formatDate = (localDate, options = {}) => localDate.toLocaleDateString('ar-SA', {
	day: 'numeric',
	month: 'short',
	year: 'numeric',
	...options
});

/**
 * Formats local date to local time string
 * @param {Date} localDate
 * @param {object} options - optional ;)
 * @returns {string}
 */
const formatTime = (localDate, options = {}) => localDate.toLocaleTimeString('ar-SA', options);

/**
 * Converts date ISO string to milliseconds (since midnight 01 January, 1970 UTC)
 * @param {string} dateISO
 * @returns {number}
 */
const dateISOtoMs = dateISO => new Date(dateISO).valueOf();

/**
 * Subtracts a certain number of days from a date
 * @param {Date} date
 * @param {number} numDays
 * @returns {Date}
 */
const subtractDaysFromDate = (date, numDays = 1) => {
	// javascript's Date automatically takes months / negative days into account.
	date.setUTCDate(date.getUTCDate() - numDays);
	return date;
};

/**
 * Subtracts a certain number of months from a date
 * @param {Date} date
 * @param {number} numMonths
 * @returns {Date}
 */
const subtractMonthsFromDate = (date, numMonths = 1) => {
	// javascript's Date automatically takes months / negative days into account.
	date.setUTCMonth(date.getUTCMonth() - numMonths);
	return date;
};

/**
 * Returns if a date is between a range
 * @param {Date} date
 * @param {Date} startDate
 * @param {Date} endDate
 * @returns {boolean}
 */
const betweenDateRange = (date, startDate, endDate) => {
	const dateMS = date.getTime();
	const startMS = startDate?.getTime() || 0;
	const endMS = endDate?.getTime() || Infinity;

	return startMS <= dateMS && dateMS <= endMS;
};

/**
 * Returns the dataset path prefix for a specific date.
 * @param {Date} date
 */
const calcPrefixFromDate = date => {
	const year2digits = String(date.getUTCFullYear()).slice(-2);
	const paddedMonth = String(date.getUTCMonth() + 1).padStart(2, '0');
	const paddedDate = String(date.getUTCDate()).padStart(2, '0');

	return `${year2digits}/${paddedMonth}${paddedDate}_`;
};

/**
 * Converts a local date to noon UTC.
 * @param {Date} localDate
 * @returns {Date}
 */
const localDateToNoonUTC = localDate => {
	const dateStrUTC = localDate.toISOString().split('T')[0];
	return new Date(`${dateStrUTC}T12:00:00.000Z`);
};

/**
 * Creates a Date from a UTC date string by adding local time
 * @param {string} utcDateStr
 * @returns {Date}
 */
const getDateFromUTCDateStr = utcDateStr => {
	const inputDateUTC = utcDateStr.split('T')[0];
	const inputDate = new Date(utcDateStr);
	// Check to see if input date is valid.
	if (isNaN(inputDate.valueOf())) {
		return null;
	}

	const nowDate = new Date();

	// If now is before the input date, we can use the current time, otherwise noon UTC.
	const timeUTC = nowDate.valueOf() < inputDate.valueOf() ?
		nowDate.toISOString().split('T')[1] :
		'12:00:00.000Z';

	return new Date(`${inputDateUTC}T${timeUTC}`);
};


/**
 * Uses current date and time with input date string in the format YYYY-MM-DD
 * to create a local Date.
 * @param {string} localDateStr format YYYY-MM-DD
 * @returns {Date}
 */
const getDateFromLocalDateStr = localDateStr => {
	const inputDate = new Date(localDateStr);
	// Check to see if input date is valid.
	if (isNaN(inputDate.valueOf())) {
		return null;
	}

	const inputDateUTC = inputDate.toISOString().split('T')[0];
	const nowDate = new Date();

	// If now is before the input date, we can use the current time, otherwise noon UTC.
	const timeUTC = nowDate.valueOf() < inputDate.valueOf() ?
		nowDate.toISOString().split('T')[1] :
		'12:00:00.000Z';

	return new Date(`${inputDateUTC}T${timeUTC}`);
};


/**
 * Makes an exact clone of a date
 * @param {Date} date
 * @returns {Date}
 */
const cloneDate = date => new Date(date);

/**
 * Calculates the month difference between two dates.
 * @param {Date} dateFrom
 * @param {Date} dateTo
 * @returns {number}
 */
const monthDifference = (dateFrom, dateTo) => dateTo.getUTCMonth() - dateFrom.getUTCMonth()
   + (12 * (dateTo.getUTCFullYear() - dateFrom.getUTCFullYear()));

/**
 * Calculates the day difference between two dates.
 * @param {Date} dateFrom
 * @param {Date} dateTo
 * @returns {number}
 */
const dayDifference = (dateFrom, dateTo) => {
	const msPerDay = (24 * 60 * 60 * 1000);
	return Math.round((dateTo.valueOf() - dateFrom.valueOf()) / msPerDay);
};

/**
 * Converts a date to a UTC string in the format YYYY-MM-DD
 * @param {Date}
 * @returns {string}
 */
const dateToUTCString = date => date.toISOString().split('T')[0];


/**
 * Converts a local date to a string in the format YYYY-MM-DD
 * Timezone is taken into account as it's a local date.
 * @param {Date}
 * @returns {string}
 */
const localDateToString = date => `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;


/**
 * Steps backwards in time from the start date to retrieve an array of dates.
 * Skips any missing dates.
 * Start and end dates are assumed to be UTC.
 * @param {object} params
 * @param {string} params.frequency
 * @param {Date} params.startDate string in format "YYYY-MM-DD"
 * @param {Date} params.endDate
 * @param {Array<string>} params.missingDates
 * @param {number} howMany
 * @returns {Array<Date>}
 */
const getLatestDates = ({ frequency, startDate, endDate, missingDates = [] }, howMany = 7) => {
	// Date of latest data starts at the endDate (usually today).
	const startDateMs = startDate.valueOf();
	const latestDate = cloneDate(endDate);
	const monthly = frequency === 'monthly';

	const latestDates = [...Array(howMany).keys()].map(num => {
		// Convert local date to UTC string to match missingDates UTC strings.
		let latestDateUTCStr = dateToUTCString(latestDate);

		// Check whether current date is missing (and we're not before the start). Go back a day/month if so.
		while (missingDates.includes(latestDateUTCStr) && latestDate.valueOf() >= startDateMs) {
			if (monthly) {
				subtractMonthsFromDate(latestDate, 1);
			} else {
				subtractDaysFromDate(latestDate, 1);
			}

			latestDateUTCStr = dateToUTCString(latestDate);
		}

		// Make sure we're not going earlier than the start date.
		if (latestDate.valueOf() < startDateMs) {
			return false;
		}

		// Make a copy to allow subtraction before return.
		const currentDate = cloneDate(latestDate);

		// Take off a day/month for the next loop.
		if (monthly) {
			subtractMonthsFromDate(latestDate, 1);
		} else {
			subtractDaysFromDate(latestDate, 1);
		}

		return currentDate;
	}).filter(date => Boolean(date));

	// Reverse the array so the latest date is the last item
	return latestDates.reverse();
};

/**
 * Current time in case we need to add it to the end of url to make sure it does not use browser caching
 */
const getUrlTime = () => Date.now() / (1000 * 60 * 30);

/**
 * Makes sure we can add commas and a maximum number of decimal places without float inaccuracies
 * @param {number} number
 * @param {number} dps
 * @returns {string}
 */
const formatNumber = (number, dps = 0) => number.toLocaleString(undefined, { maximumFractionDigits: dps });

/**
 * Identifies a default isCelcius boolean based on the navigator language string.
 * Anything within America (en-US) will default to fahrenheit (isCelcius = false).
 * Otherwise isCelcius is true.
 * @returns {boolean}
 */
const isLanguageCelcius = () => {
	// A list of language strings that use F by default (https://worldpopulationreview.com/country-rankings/countries-that-use-fahrenheit)
	const excluded = ['en-US'];

	const { language } = window?.navigator || {};
	return !excluded.includes(language);
};

/**
 * creates a base64 svg img source placeholder to prevent page reflow when loading images of known dimensions
 * article here: https://css-tricks.com/preventing-content-reflow-from-lazy-loaded-images/
 */
const placeholderSrc = (width, height) => `data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}"%3E%3C/svg%3E`;


const EntoAr = function (number) {
	const id = [
		'۰',
		'۱',
		'۲',
		'۳',
		'٤',
		'۵',
		'٦',
		'۷',
		'۸',
		'۹'
	];
	const new_number = number.replace(/[0-9]/g, w => id[Number(w)]);
	return new_number;
};
export {
	isEmptyObj,
	compareObjs,
	formatDate,
	formatTime,
	dateISOtoMs,
	subtractDaysFromDate,
	subtractMonthsFromDate,
	betweenDateRange,
	calcPrefixFromDate,
	localDateToNoonUTC,
	getDateFromUTCDateStr,
	cloneDate,
	monthDifference,
	dayDifference,
	dateToUTCString,
	localDateToString,
	getDateFromLocalDateStr,
	getLatestDates,
	getUrlTime,
	formatNumber,
	isLanguageCelcius,
	placeholderSrc,
	EntoAr

};

